import { useState, type ChangeEvent, type FormEvent } from "react";
import { C, font } from "../theme";
import { Card } from "./Card";
import { Label } from "./Label";
import { Button } from "./Button";
import { FieldInput, FieldTextarea } from "./Field";
import { Select } from "./Select";
import type { CategoryDto, CreateProductRequest, ProductDto } from "../types";

const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_IMAGE_BYTES = 2 * 1024 * 1024;

function validateImageFile(file: File | null): string | null {
  if (!file) return null;
  if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) return "A imagem deve ser JPEG, PNG ou WEBP.";
  if (file.size > MAX_IMAGE_BYTES) return "A imagem deve ter no máximo 2MB.";
  return null;
}

interface ProductFormProps {
  mode: "create" | "edit";
  initial?: ProductDto;
  categories: CategoryDto[];
  submitting: boolean;
  submitError: string | null;
  onSubmit: (payload: CreateProductRequest, imageFile?: File | null) => void;
  onCancel: () => void;
}

type FieldErrors = Partial<Record<"name" | "description" | "sku" | "barcode" | "price" | "costPrice" | "stockQuantity" | "minStockAlert", string>>;

function toNullable(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

function toNullableNumber(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === "") return null;
  const n = Number(trimmed);
  return Number.isNaN(n) ? null : n;
}

export function ProductForm({ mode, initial, categories, submitting, submitError, onSubmit, onCancel }: ProductFormProps) {
  const [name, setName] = useState(initial?.name ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [sku, setSku] = useState(initial?.sku ?? "");
  const [barcode, setBarcode] = useState(initial?.barcode ?? "");
  const [price, setPrice] = useState(initial ? String(initial.price) : "");
  const [costPrice, setCostPrice] = useState(initial?.costPrice != null ? String(initial.costPrice) : "");
  const [stockQuantity, setStockQuantity] = useState(initial ? String(initial.stockQuantity) : "");
  const [minStockAlert, setMinStockAlert] = useState(initial?.minStockAlert != null ? String(initial.minStockAlert) : "");
  const [categoryId, setCategoryId] = useState(initial?.categoryId ?? "");
  const [isActive, setIsActive] = useState(initial?.isActive ?? true);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    const validation = validateImageFile(file);
    setImageError(validation);
    if (validation || !file) {
      setImageFile(null);
      setImagePreview(null);
      return;
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const validate = (): FieldErrors => {
    const next: FieldErrors = {};
    const priceValue = toNullableNumber(price);
    const costValue = toNullableNumber(costPrice);
    const stockValue = toNullableNumber(stockQuantity);
    const alertValue = toNullableNumber(minStockAlert);

    if (name.trim() === "") next.name = "Informe o nome";
    else if (name.length > 120) next.name = "Nome deve ter no máximo 120 caracteres";

    if (description.length > 500) next.description = "Descrição deve ter no máximo 500 caracteres";
    if (sku.length > 60) next.sku = "SKU deve ter no máximo 60 caracteres";
    if (barcode.length > 60) next.barcode = "Código de barras deve ter no máximo 60 caracteres";

    if (priceValue === null || priceValue <= 0) next.price = "Preço deve ser maior que zero";
    if (costValue !== null && costValue < 0) next.costPrice = "Custo não pode ser negativo";

    if (stockValue === null) next.stockQuantity = "Informe o estoque";
    else if (stockValue < 0) next.stockQuantity = "Estoque não pode ser negativo";

    if (alertValue !== null && alertValue < 0) next.minStockAlert = "Alerta mínimo não pode ser negativo";

    return next;
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const nextErrors = validate();
    const imageValidation = validateImageFile(imageFile);
    if (imageValidation) setImageError(imageValidation);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0 || imageValidation) return;

    const payload: CreateProductRequest = {
      name: name.trim(),
      price: toNullableNumber(price)!,
      stockQuantity: toNullableNumber(stockQuantity)!,
      isActive,
    };
    const descriptionValue = toNullable(description);
    const skuValue = toNullable(sku);
    const barcodeValue = toNullable(barcode);
    const costValue = toNullableNumber(costPrice);
    const alertValue = toNullableNumber(minStockAlert);
    const categoryValue = toNullable(categoryId);

    if (descriptionValue) payload.description = descriptionValue;
    if (skuValue) payload.sku = skuValue;
    if (barcodeValue) payload.barcode = barcodeValue;
    if (costValue !== null) payload.costPrice = costValue;
    if (alertValue !== null) payload.minStockAlert = alertValue;
    if (categoryValue) payload.categoryId = categoryValue;

    onSubmit(payload, imageFile);
  };

  const labelStyle: React.CSSProperties = {
    display: "block",
    fontFamily: font,
    fontSize: 12,
    fontWeight: 600,
    color: C.gray,
    marginBottom: 4,
    marginTop: 12,
  };

  const errorStyle: React.CSSProperties = { color: C.red, fontSize: 11.5, marginTop: 4 };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(17,17,17,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 50,
        padding: 16,
      }}
    >
      <Card style={{ width: "100%", maxWidth: 560, maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Label style={{ color: C.ink, fontSize: 13, letterSpacing: 0.5 }}>
            {mode === "create" ? "Novo produto" : "Editar produto"}
          </Label>
          <Button variant="ghost" onClick={onCancel} aria-label="Fechar" style={{ fontSize: 16, lineHeight: 1 }}>
            ✕
          </Button>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <label htmlFor="product-image" style={labelStyle}>Foto do produto</label>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 4 }}>
            {imagePreview ? (
              <img
                src={imagePreview}
                alt="Preview da imagem"
                style={{ width: 56, height: 56, borderRadius: 10, objectFit: "cover", flexShrink: 0 }}
              />
            ) : initial?.imageUrl ? (
              <img
                src={initial.imageUrl}
                alt="Preview da imagem"
                style={{ width: 56, height: 56, borderRadius: 10, objectFit: "cover", flexShrink: 0 }}
              />
            ) : (
              <div
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: 10,
                  background: C.faint,
                  color: C.light,
                  fontWeight: 700,
                  fontSize: 18,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {(initial?.name ?? "?").charAt(0).toUpperCase()}
              </div>
            )}
            <div style={{ flex: 1 }}>
              <FieldInput
                id="product-image"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleImageChange}
                disabled={submitting}
                style={{ fontSize: 12.5 }}
              />
              <div style={{ fontSize: 10.5, color: C.light, marginTop: 3 }}>JPEG, PNG ou WEBP · máx. 2MB</div>
            </div>
          </div>
          {imageError && <div style={errorStyle}>{imageError}</div>}

          <label htmlFor="product-name" style={labelStyle}>Nome *</label>
          <FieldInput id="product-name" value={name} onChange={e => setName(e.target.value)} placeholder="Ex: Isotônico 500ml" maxLength={120} />
          {errors.name && <div style={errorStyle}>{errors.name}</div>}

          <label htmlFor="product-description" style={labelStyle}>Descrição</label>
          <FieldTextarea id="product-description" value={description} onChange={e => setDescription(e.target.value)} maxLength={500} />
          {errors.description && <div style={errorStyle}>{errors.description}</div>}

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label htmlFor="product-sku" style={labelStyle}>SKU</label>
              <FieldInput id="product-sku" value={sku} onChange={e => setSku(e.target.value)} maxLength={60} />
              {errors.sku && <div style={errorStyle}>{errors.sku}</div>}
            </div>
            <div>
              <label htmlFor="product-barcode" style={labelStyle}>Código de barras</label>
              <FieldInput id="product-barcode" value={barcode} onChange={e => setBarcode(e.target.value)} maxLength={60} />
              {errors.barcode && <div style={errorStyle}>{errors.barcode}</div>}
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label htmlFor="product-price" style={labelStyle}>Preço (R$) *</label>
              <FieldInput id="product-price" type="number" min={0} step="0.01" value={price} onChange={e => setPrice(e.target.value)} />
              {errors.price && <div style={errorStyle}>{errors.price}</div>}
            </div>
            <div>
              <label htmlFor="product-cost" style={labelStyle}>Custo (R$)</label>
              <FieldInput id="product-cost" type="number" min={0} step="0.01" value={costPrice} onChange={e => setCostPrice(e.target.value)} />
              {errors.costPrice && <div style={errorStyle}>{errors.costPrice}</div>}
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label htmlFor="product-stock" style={labelStyle}>{mode === "create" ? "Estoque inicial *" : "Estoque *"}</label>
              <FieldInput id="product-stock" type="number" min={0} step="1" value={stockQuantity} onChange={e => setStockQuantity(e.target.value)} />
              {errors.stockQuantity && <div style={errorStyle}>{errors.stockQuantity}</div>}
            </div>
            <div>
              <label htmlFor="product-alert" style={labelStyle}>Mínimo p/ alerta</label>
              <FieldInput id="product-alert" type="number" min={0} step="1" value={minStockAlert} onChange={e => setMinStockAlert(e.target.value)} />
              {errors.minStockAlert && <div style={errorStyle}>{errors.minStockAlert}</div>}
            </div>
          </div>

          <label htmlFor="product-category" style={labelStyle}>Categoria</label>
          <Select id="product-category" value={categoryId} options={[{ value: "", label: "Sem categoria" }, ...categories.map(c => ({ value: c.id, label: c.name }))]} onChange={setCategoryId} style={{ width: "100%" }} />

          <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 14, fontSize: 12.5, fontWeight: 600, color: C.gray }}>
            <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} />
            Produto ativo
          </label>

          {submitError && (
            <div style={{ marginTop: 14, padding: 10, borderRadius: 8, background: "C.redSoft", color: C.red, fontSize: 12.5 }}>
              {submitError}
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
            <Button variant="outline" onClick={onCancel} disabled={submitting}>Cancelar</Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? "Salvando..." : mode === "create" ? "Criar produto" : "Salvar alterações"}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
